<template>
  <!-- The digola lockup as a button: "leave LingoTalk, back to the portal". -->
  <button type="button" class="digola" :title="t('portal')" @click="goToPortal">
    <img :src="logoDigola" alt="digola Logo" />
  </button>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import logoDigola from '@/assets/logo/digola.svg?no-inline'
import { leaveForPortal } from '@/lib/portal-exit'

const { t } = useI18n()

/**
 * Hand the therapist back to the portal shell. Only the parent frame can
 * navigate itself, so this goes over the embed bridge as `portal:navigate`,
 * which the portal resolves to a router push onto its own dashboard.
 *
 * It has to be that and never `window.top.location`: the portal's vault — the
 * therapist's decryption keys — lives in memory in its user store and is gone
 * on a page load, so a real navigation would land them on the dashboard with
 * every patient name locked behind the password prompt again. A router push
 * keeps the store, so the keys survive. The portal also only accepts an
 * allow-listed target, which is why this sends `'home'` and not a URL.
 *
 * Imported lazily, exactly as App.vue does: this button sits in layouts that
 * the native patient bundle also ships, so a top-level import would drag the
 * whole embed bridge into it.
 *
 * `requestPortalNavigation` reports whether it had a channel to send on. It
 * does whenever the portal has completed the handshake, locked vault included,
 * so inside the portal that is the whole story. Without a channel there is no
 * portal listening: either we are not in the portal at all (standalone web
 * build), where `leaveForPortal` navigates this window to the portal origin,
 * or the handshake has not completed / has ended, where the embed host screen
 * is already showing its own status and nothing more can be done from here.
 */
async function goToPortal(): Promise<void> {
  const { useEmbedBridge } = await import('@/composables/useEmbedBridge')
  if (useEmbedBridge().requestPortalNavigation('home')) return
  leaveForPortal()
}
</script>

<style scoped lang="css">
button.digola {
  /* Never let a flex row shrink the mark away. */
  flex: 0 0 auto;

  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;

  img {
    /* Pin the HEIGHT and let the aspect ratio drive the width. The lockup is
       two lines (wordmark + "digitale Therapie"), so it needs a little more
       height than the single-line LingoTalk wordmark beside it in the header
       to read at the same optical weight. */
    width: auto;
    height: 43px;

    /* Kill the inline-image baseline gap inside the button box. */
    display: block;
  }

  /* No button chrome — just the mark, plus the same rounded hover shadow the
     back/help buttons use (Button.vue). The negative margins cancel the hover
     padding so the mark stays optically where a plain image would sit. */
  @media (hover: hover) and (pointer: fine) {
    margin: 0 -0.5rem;
    padding: 0.25rem 0.5rem;

    &:hover {
      border-radius: 15px;
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    }
  }
}
</style>

<i18n>
{
  "de": {
    "portal": "Zurück zum Portal"
  },
  "en": {
    "portal": "Back to the portal"
  }
}
</i18n>
